import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { imgUrl } from "@/lib/api";

export default function Lightbox({ photos, index, onClose, onNavigate }) {
  const touchX = useRef(null);
  const open = index != null && photos[index];

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onNavigate((index - 1 + photos.length) % photos.length);
      if (e.key === "ArrowRight") onNavigate((index + 1) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, index, photos.length, onClose, onNavigate]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx < -50) onNavigate((index + 1) % photos.length);
    if (dx > 50) onNavigate((index - 1 + photos.length) % photos.length);
    touchX.current = null;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
          data-testid="photo-lightbox" onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
          className="fixed inset-0 z-[100] bg-[#111110]/97 backdrop-blur-sm flex items-center justify-center">
          <button onClick={onClose} data-testid="lightbox-close" aria-label="Close"
            className="absolute top-5 right-5 text-[#F9F9F8]/70 hover:text-[#F9F9F8] transition-colors z-10 p-2">
            <X size={26} strokeWidth={1.25} />
          </button>
          <p className="absolute top-7 left-6 text-[#F9F9F8]/50 text-[11px] uppercase tracking-[0.25em]">
            {index + 1} / {photos.length}
          </p>
          {photos.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onNavigate((index - 1 + photos.length) % photos.length); }}
                data-testid="lightbox-prev" aria-label="Previous"
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-[#F9F9F8]/60 hover:text-[#F9F9F8] transition-colors p-3 z-10">
                <ChevronLeft size={34} strokeWidth={1} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); onNavigate((index + 1) % photos.length); }}
                data-testid="lightbox-next" aria-label="Next"
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-[#F9F9F8]/60 hover:text-[#F9F9F8] transition-colors p-3 z-10">
                <ChevronRight size={34} strokeWidth={1} />
              </button>
            </>
          )}
          <motion.img key={photos[index].id || index} src={imgUrl(photos[index].url)}
            alt={photos[index].caption || "Photograph by Bahare Salehnia"}
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[82vh] object-contain" />
          {photos[index].caption && (
            <p className="absolute bottom-6 left-0 right-0 text-center text-[#F9F9F8]/60 text-xs tracking-[0.2em] uppercase">
              {photos[index].caption}
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
